import { AssetDeletionView } from '@/api/generatedApi';
import { resetAssetListQueries, resetDeletedAssetQueries } from '@/hooks/api/assets/resetDeletedAssetQueries';
import { ACTIVE_ASSET_DELETIONS_QUERY_KEY } from '@/hooks/api/assets/useActiveAssetDeletions';
import { useToast } from '@/hooks/useToast';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { pluralize } from '@/utils/format';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useAssetDeletionDialog } from './useAssetDeletionDialog';

const UNKNOWN_RESULT = 'Could not load the result of this delete. Check the list to confirm.';

interface Totals {
  total: number;
  deleted: number;
  missing: number;
  hasFailures: boolean;
}

export const UNFINISHED_JOB_RECHECK_MS = 3000;

const NO_TOTALS: Totals = { total: 0, deleted: 0, missing: 0, hasFailures: false };

const newRun = () => ({
  all: NO_TOTALS,
  own: NO_TOTALS,
  couldNotLoad: false,
  pendingLookups: 0,
  lookedUp: new Set<string>(),
  stillRunning: new Set<string>(),
});

const addJob = (totals: Totals, job: AssetDeletionView): Totals => ({
  total: totals.total + job.totalAssets,
  deleted: totals.deleted + job.deletedAssets,
  missing: totals.missing + job.missingAssets,
  hasFailures: totals.hasFailures || job.status === 'FAILED' || job.failedAssets > 0,
});

const describeResult = ({ total, deleted, missing }: Totals) => {
  const failed = total - deleted - missing;
  const summary = `Deleted ${pluralize(deleted, 'asset', 'assets')}`;
  const extras = [];
  if (failed > 0) extras.push(`Failed to delete ${pluralize(failed, 'asset', 'assets')}.`);
  if (missing > 0) extras.push(`${pluralize(missing, 'asset', 'assets')} already gone.`);
  return extras.length > 0 ? `${summary}. ${extras.join(' ')}` : summary;
};

const isUnfinished = (job: AssetDeletionView) => job.status === 'PENDING' || job.status === 'RUNNING';

export const useFinishedDeletionReport = (isIdle: boolean) => {
  const { getTenantId } = useTenants();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [finished, setFinished] = useState(NO_TOTALS);
  const run = useRef(newRun());
  const isIdleReference = useRef(isIdle);
  isIdleReference.current = isIdle;

  useEffect(
    () => () => {
      run.current = newRun();
    },
    []
  );

  const reportOnceAllAreDone = () => {
    const { all, own, couldNotLoad, pendingLookups, stillRunning } = run.current;
    if (pendingLookups > 0 || stillRunning.size > 0 || !isIdleReference.current) return;
    if (all.total === 0 && !couldNotLoad) return;
    run.current = newRun();
    setFinished(NO_TOTALS);
    if (own.total > 0) resetDeletedAssetQueries(queryClient);
    else resetAssetListQueries(queryClient);
    if (useAssetDeletionDialog.getState().isOpen) {
      useAssetDeletionDialog.getState().setResult(couldNotLoad ? UNKNOWN_RESULT : describeResult(all));
    } else if (couldNotLoad) {
      showToast({ message: UNKNOWN_RESULT, type: 'warning' });
    } else if (own.total > 0) {
      showToast({ message: describeResult(own), type: own.hasFailures ? 'error' : 'success' });
    }
  };

  const addFinishedJob = (currentRun: ReturnType<typeof newRun>, job: AssetDeletionView) => {
    currentRun.all = addJob(currentRun.all, job);
    if (job.startedByRequester) currentRun.own = addJob(currentRun.own, job);
    setFinished(currentRun.all);
  };

  const reportFinishedJob = async (id: string) => {
    const currentRun = run.current;
    if (currentRun.lookedUp.has(id)) return;
    currentRun.lookedUp.add(id);
    currentRun.pendingLookups += 1;
    try {
      const { data } = await api.assets.getAssetDeletion(getTenantId(), id);
      if (run.current !== currentRun) return;
      if (isUnfinished(data)) {
        currentRun.lookedUp.delete(id);
        currentRun.stillRunning.add(id);
        void queryClient.invalidateQueries({ queryKey: [ACTIVE_ASSET_DELETIONS_QUERY_KEY] });
        setTimeout(() => {
          if (run.current === currentRun && currentRun.stillRunning.has(id)) void reportFinishedJob(id);
        }, UNFINISHED_JOB_RECHECK_MS);
      } else {
        currentRun.stillRunning.delete(id);
        addFinishedJob(currentRun, data);
      }
    } catch {
      currentRun.stillRunning.delete(id);
      currentRun.couldNotLoad = true;
    } finally {
      currentRun.pendingLookups -= 1;
    }
    if (run.current === currentRun) reportOnceAllAreDone();
  };

  return { finished, reportFinishedJob, reportOnceAllAreDone };
};
